import React from 'react';
import PropTypes from 'prop-types';

import { DropdownSimple } from '../Dropdown';
import TicketSVG from '../../svg/icons/ic_ticket_20x20.svg';
import { getTranslation } from '../../providers/TranslationProvider';

import './TicketQuantitySelector.scss';

export const buildOptions = (maxAvailable, minAvailable, type, eventLargePartyMessage = null) => {
  const options = [];

  for (let i = minAvailable; i <= maxAvailable; i += 1) {
    let label = i === 1 ? getTranslation({
      translationKey: 'one_ticket',
      fallback: '1 Ticket',
    })
      : getTranslation({
        translationKey: 'number_tickets',
        translationValues: { seats: i },
        fallback: `${i} Tickets`,
      });

    if (type === 'seated') {
      label = i === 1 ? getTranslation({
        translationKey: 'one_guest',
        fallback: '1 Guest',
      })
        : getTranslation({
          translationKey: 'number_guests',
          translationValues: { number: i },
          fallback: `${i} Guests`,
        });
    }
    options.push({
      value: `${i}`, label, key: `${i}`,
    });
  }

  if (eventLargePartyMessage) {
    const maxPlusOne = maxAvailable + 1;
    let label;

    if (type === 'seated') {
      label = getTranslation({
        translationKey: 'number_guests_plus',
        translationValues: { seats: maxPlusOne },
        fallback: `${maxPlusOne}+ Guests`,
      });
    } else {
      label = getTranslation({
        translationKey: 'number_tickets_plus',
        translationValues: { seats: maxPlusOne },
        fallback: `${maxPlusOne}+ Tickets`,
      });
    }

    options.push({
      value: `${maxPlusOne}`, label: `${label}`, key: `${maxPlusOne}`,
    });
  }

  return options;
};

const generateAriaLabel = (eventName) => {
  if (!eventName) {
    return getTranslation({ translationKey: 'number_tickets_label', fallback: 'Number of Tickets' });
  }

  const translationKeyValue = 'number_tickets_for_event_label';
  const eventAriaLabel = `Number of tickets for ${eventName}`;

  return getTranslation({
    translationKey: translationKeyValue,
    translationValues: {
      eventTitle: eventName,
    },
    fallback: eventAriaLabel,
  });
};

const TicketQuantitySelector = (props) => {
  const {
    eventName,
    onChange,
    maxAvailable,
    selected,
    type,
    minAvailable,
    eventLargePartyMessage,
  } = props;
  let selectedValue = selected;

  if (selected < minAvailable) {
    selectedValue = minAvailable;
    onChange({ currentTarget: { value: minAvailable } });
  }

  if (selected > maxAvailable && !eventLargePartyMessage) {
    selectedValue = maxAvailable;
    onChange({ currentTarget: { value: maxAvailable } });
  }

  const ariaLabelCopy = generateAriaLabel(eventName, selectedValue);
  if (maxAvailable === 0) return null;

  return (
    <DropdownSimple
      ariaLabel={ariaLabelCopy}
      className="TicketQuantitySelector"
      icon={TicketSVG}
      name="tickets"
      onChange={onChange}
      options={buildOptions(maxAvailable, minAvailable, type, eventLargePartyMessage)}
      style={{
        fontWeight: 'bold',
        height: '40px',
        width: '160px',
      }}
      value={String(selectedValue)}
    />
  );
};

TicketQuantitySelector.propTypes = {
  maxAvailable: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.number,
  eventName: PropTypes.string,
  type: PropTypes.string,
  minAvailable: PropTypes.number,
  eventLargePartyMessage: PropTypes.string,
};

TicketQuantitySelector.defaultProps = {
  maxAvailable: 20,
  selected: '1',
  eventName: null,
  type: 'general',
  eventLargePartyMessage: null,
  minAvailable: 1,
};

TicketQuantitySelector.displayName = 'TicketQuantitySelector';
export default TicketQuantitySelector;
